/**
 * Performance Enhancements for Shopify Theme
 * Reduces main thread blocking, optimizes rendering, and improves Core Web Vitals
 */

(function() {
  'use strict';

  // ====================================
  // 1. IDLE TASK SCHEDULER
  // ====================================

  /**
   * Schedule non-critical tasks during idle periods
   */
  const taskQueue = [];
  let isProcessingQueue = false;

  function scheduleIdleTask(task, options = {}) {
    const priority = options.priority || 'low';
    taskQueue.push({ task, priority, timestamp: Date.now() });

    if (!isProcessingQueue) {
      processIdleQueue();
    }
  }

  function processIdleQueue() {
    if (taskQueue.length === 0) {
      isProcessingQueue = false;
      return;
    }

    isProcessingQueue = true;

    // Use requestIdleCallback if available, otherwise setTimeout
    const scheduleWork = 'requestIdleCallback' in window
      ? (callback) => requestIdleCallback(callback, { timeout: 2000 })
      : (callback) => setTimeout(callback, 1);

    scheduleWork((deadline) => {
      while ((deadline.timeRemaining() > 0 || deadline.didTimeout) && taskQueue.length > 0) {
        const { task } = taskQueue.shift();
        try {
          task();
        } catch (error) {
          console.error('Idle task error:', error);
        }
      }

      processIdleQueue();
    });
  }

  // Expose globally for use in other scripts
  window.shopifyPerf = window.shopifyPerf || {};
  window.shopifyPerf.scheduleIdleTask = scheduleIdleTask;

  // ====================================
  // 2. LAZY LOAD SECTIONS
  // ====================================

  /**
   * Lazy load below-the-fold sections to improve initial load
   */
  function lazyLoadSections() {
    const sections = document.querySelectorAll('[data-lazy-section]');

    if ('IntersectionObserver' in window) {
      const observer = new IntersectionObserver((entries, observer) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            const section = entry.target;
            const scriptUrl = section.getAttribute('data-lazy-script');

            if (scriptUrl) {
              loadScript(scriptUrl);
            }

            section.classList.add('section-loaded');
            observer.unobserve(section);
          }
        });
      }, {
        rootMargin: '50px 0px',
        threshold: 0.01
      });

      sections.forEach(section => observer.observe(section));
    } else {
      // Fallback: load all sections
      sections.forEach(section => {
        const scriptUrl = section.getAttribute('data-lazy-script');
        if (scriptUrl) scheduleIdleTask(() => loadScript(scriptUrl));
      });
    }
  }

  function loadScript(url) {
    if (document.querySelector(`script[src="${url}"]`)) return;

    const script = document.createElement('script');
    script.src = url;
    script.async = true;
    document.head.appendChild(script);
  }

  // ====================================
  // 3. DEBOUNCE SCROLL HANDLERS
  // ====================================

  /**
   * Optimize scroll handlers to prevent jank
   */
  function optimizeScrollHandlers() {
    let scrollTimer;
    let lastScrollY = window.scrollY;
    const scrollThrottle = 16; // ~60fps

    window.addEventListener('scroll', () => {
      const currentScrollY = window.scrollY;

      if (Math.abs(currentScrollY - lastScrollY) < 5) return;

      clearTimeout(scrollTimer);
      scrollTimer = setTimeout(() => {
        lastScrollY = currentScrollY;

        // Dispatch custom event for other scripts to listen to
        document.dispatchEvent(new CustomEvent('optimizedScroll', {
          detail: { scrollY: currentScrollY }
        }));
      }, scrollThrottle);
    }, { passive: true });
  }

  // ====================================
  // 4. OPTIMIZE IMAGES
  // ====================================

  /**
   * Decode images asynchronously to prevent blocking
   */
  function optimizeImages() {
    scheduleIdleTask(() => {
      const images = document.querySelectorAll('img[loading="lazy"]');

      if ('decode' in HTMLImageElement.prototype) {
        images.forEach(img => {
          if (img.complete && img.naturalHeight !== 0) {
            img.decode().catch(() => {
              // Decode failed, image will render normally
            });
          }
        });
      }
    });
  }

  // ====================================
  // 5. REDUCE LAYOUT THRASHING
  // ====================================

  /**
   * Batch DOM reads and writes to prevent layout thrashing
   */
  const readQueue = [];
  const writeQueue = [];
  let isScheduled = false;

  function batchRead(callback) {
    readQueue.push(callback);
    scheduleFlush();
  }

  function batchWrite(callback) {
    writeQueue.push(callback);
    scheduleFlush();
  }

  function scheduleFlush() {
    if (isScheduled) return;
    isScheduled = true;

    requestAnimationFrame(() => {
      // Execute all reads first
      while (readQueue.length > 0) {
        const read = readQueue.shift();
        read();
      }

      // Then execute all writes
      while (writeQueue.length > 0) {
        const write = writeQueue.shift();
        write();
      }

      isScheduled = false;
    });
  }

  window.shopifyPerf.batchRead = batchRead;
  window.shopifyPerf.batchWrite = batchWrite;

  // ====================================
  // 6. PRECONNECT TO IMPORTANT ORIGINS
  // ====================================

  /**
   * Add preconnect hints for frequently used origins
   */
  function addPreconnects() {
    const origins = [
      'https://cdn.shopify.com',
      'https://fonts.shopifycdn.com'
    ];

    origins.forEach(origin => {
      const link = document.createElement('link');
      link.rel = 'preconnect';
      link.href = origin;
      link.crossOrigin = 'anonymous';

      if (!document.querySelector(`link[href="${origin}"]`)) {
        document.head.appendChild(link);
      }
    });
  }

  // ====================================
  // 7. OPTIMIZE FONT LOADING
  // ====================================

  /**
   * Use Font Loading API to optimize web fonts
   */
  function optimizeFonts() {
    if ('fonts' in document) {
      scheduleIdleTask(() => {
        document.fonts.ready.then(() => {
          console.log('✅ Fonts loaded successfully');
        });
      });
    }
  }

  // ====================================
  // 8. REDUCE INP (INTERACTION TO NEXT PAINT)
  // ====================================

  /**
   * Break up long tasks to improve INP
   */
  function yieldToMain() {
    return new Promise(resolve => {
      setTimeout(resolve, 0);
    });
  }

  async function processLongTask(items, callback) {
    for (let i = 0; i < items.length; i++) {
      callback(items[i]);

      // Yield every 50ms to prevent blocking
      if (i % 50 === 0) {
        await yieldToMain();
      }
    }
  }

  window.shopifyPerf.processLongTask = processLongTask;

  // ====================================
  // 9. MONITOR LONG TASKS
  // ====================================

  /**
   * Monitor and log long tasks for debugging
   */
  function monitorLongTasks() {
    if (!('PerformanceObserver' in window)) return;

    try {
      const observer = new PerformanceObserver((list) => {
        for (const entry of list.getEntries()) {
          if (entry.duration > 50) {
            console.warn(`⚠️ Long task detected: ${Math.round(entry.duration)}ms`, entry);
          }
        }
      });

      observer.observe({ entryTypes: ['longtask'] });
    } catch (e) {
      // Long task API not supported
    }
  }

  // ====================================
  // 10. INTERSECTION OBSERVER UTILITIES
  // ====================================

  /**
   * Reusable intersection observer for lazy loading
   */
  function createLazyObserver(callback, options = {}) {
    if (!('IntersectionObserver' in window)) {
      // Fallback: execute immediately
      return {
        observe: (element) => callback([{ target: element, isIntersecting: true }]),
        disconnect: () => {}
      };
    }

    const defaultOptions = {
      rootMargin: '50px 0px',
      threshold: 0.01
    };

    return new IntersectionObserver((entries, observer) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          callback([entry]);
          observer.unobserve(entry.target);
        }
      });
    }, { ...defaultOptions, ...options });
  }

  window.shopifyPerf.createLazyObserver = createLazyObserver;

  // ====================================
  // INITIALIZE ALL OPTIMIZATIONS
  // ====================================

  function init() {
    // Run critical optimizations immediately
    optimizeScrollHandlers();
    addPreconnects();

    // Schedule non-critical optimizations
    scheduleIdleTask(lazyLoadSections);
    scheduleIdleTask(optimizeImages);
    scheduleIdleTask(optimizeFonts);

    // Monitor performance in development
    if (window.location.hostname.includes('kopa.dev') ||
        window.location.hostname.includes('myshopify.com')) {
      scheduleIdleTask(monitorLongTasks);
    }

    console.log('✅ Performance enhancements initialized');
  }

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();
